import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useI18n, useLogger, useMetadata } from '~Hooks';
import { Metadata } from '~Hooks/useMetadata';
import { SettingsBlock } from '~Scenes/Settings-v2';

const KDSSettingsList: FC = () => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const meta = useMetadata();
  const logger = useLogger('settings-v2');

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (KDS Settings)', { fields: data });
    commitToStorage(data);
  }

  function updateMetadataSetting(data: Partial<Metadata>) {
    logger.info('Updating Settings (KDS Metadata)', { fields: data });
    meta.update(data);
  }

  const togglesConfig: Array<{
    label: string;
    subLabel: string;
    key: string;
    storage: any;
    // Will reverse the value of the storage
    inverted?: boolean;
    // Where the value is persisted. Defaults to device storage.
    source?: 'storage' | 'metadata';
  }> = [
    {
      label: i18n('Settings.KDS.BongOnProgress'),
      subLabel: i18n('Settings.KDS.BongOnProgress.Text'),
      key: 'KDSBongOnProgress',
      storage: storage.KDSBongOnProgress,
    },
    {
      label: i18n('Settings.KDS.BongOnReady'),
      subLabel: i18n('Settings.KDS.BongOnReady.Text'),
      key: 'KDSBongOnReady',
      storage: storage.KDSBongOnReady,
    },
    {
      label: i18n('Settings.KDS.BongOnItemProgress'),
      subLabel: i18n('Settings.KDS.BongOnItemProgress.Text'),
      key: 'KDSBongOnItemProgress',
      storage: storage.KDSBongOnItemProgress,
    },
    {
      label: i18n('Settings.KDS.TripleProgress'),
      subLabel: i18n('Settings.KDS.TripleProgress.Text'),
      key: 'tripleOrderItemProgress',
      storage: storage.tripleOrderItemProgress,
    },
    {
      label: i18n('Settings.KDS.DisableFullOrderProgress'),
      subLabel: i18n('Settings.KDS.DisableFullOrderProgress.Text'),
      key: 'disableOrderProgress',
      storage: storage.disableOrderProgress,
      inverted: true,
    },
    {
      label: i18n('Settings.KDS.DisableItemProgress'),
      subLabel: i18n('Settings.KDS.DisableItemProgress.Text'),
      key: 'disableItemProgress',
      storage: storage.disableItemProgress,
      inverted: true,
    },
    {
      label: i18n('Settings.KDS.OrderStatusFilter'),
      subLabel: i18n('Settings.KDS.OrderStatusFilter.Text'),
      key: 'KDSFilterOnWholeOrderStatus',
      storage: storage.KDSFilterOnWholeOrderStatus,
      inverted: true,
    },
    {
      label: i18n('Settings.KDS.ShowAllOrderStatuses'),
      subLabel: i18n('Settings.KDS.ShowAllOrderStatuses.Text'),
      key: 'KDSShowAllOrderStatuses',
      storage: storage.KDSShowAllOrderStatuses,
    },
    {
      label: i18n('Settings.KDS.ProgressOnlyFiltered'),
      subLabel: i18n('Settings.KDS.ProgressOnlyFiltered.Text'),
      key: 'KDSProgressOnlyFiltered',
      storage: storage.KDSProgressOnlyFiltered,
    },
    {
      label: i18n('Settings.KDS.PlaySound'),
      subLabel: i18n('Settings.KDS.PlaySound.Text'),
      key: 'KDSPlayNewOrderSound',
      storage: storage.KDSPlayNewOrderSound,
    },
    {
      label: i18n('Settings.KDS.ShowOrderStatusInTSR'),
      subLabel: i18n('Settings.KDS.ShowOrderStatusInTSR.Text'),
      key: 'KDSShowOrderStatusInTSR',
      storage: storage.KDSShowOrderStatusInTSR,
    },
    {
      label: i18n('Settings.KDS.GroupItemsByCourseType'),
      subLabel: i18n('Settings.KDS.GroupItemsByCourseType.Text'),
      key: 'KDSGroupItemsByCourseType',
      storage: storage.KDSGroupItemsByCourseType,
    },
    {
      label: i18n('Settings.KDS.CourseOrdersMode'),
      subLabel: i18n('Settings.KDS.CourseOrdersMode.Text'),
      key: 'useCoursesInKDS',
      storage: meta.data.useCoursesInKDS,
      source: 'metadata',
    },
    {
      label: i18n('Settings.KDS.HoldFirstCourse'),
      subLabel: i18n('Settings.KDS.HoldFirstCourse.Text'),
      key: 'holdFirstCourse',
      storage: storage.holdFirstCourse,
    },
    {
      label: i18n('Settings.KDS.HideOrderHeader'),
      subLabel: i18n('Settings.KDS.HideOrderHeader.Text'),
      key: 'KDSHideOrderHeader',
      storage: storage.KDSHideOrderHeader,
    },
  ];

  if (storageLoading) {
    return null;
  }

  return (
    <>
      <Heading level={4} bottom={8} color="var(--colorWhite)">
        {i18n('Settings.Title')}
      </Heading>
      <SettingsBlock>
        {togglesConfig.map((toggle) => (
          <Toggle
            key={toggle.key}
            settingKey={toggle.key}
            label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
            description={toggle.subLabel}
            initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
            onToggle={() =>
              toggle.source === 'metadata'
                ? updateMetadataSetting({ [toggle.key]: !toggle.storage } as Partial<Metadata>)
                : updateStorage({ [toggle.key]: !toggle.storage })
            }
            fullWidth
            darkBackground
            lightText
          />
        ))}
      </SettingsBlock>
    </>
  );
};

export default KDSSettingsList;
